import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { SITE_URL } from '@/constants';

export default function SugargooGuidePage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Article Header */}
      <main>
        <section className="pt-28 pb-12 md:pt-32 md:pb-16 bg-background-100">
          <div className="section-container">
            <div className="max-w-3xl mx-auto">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 mb-6">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-primary-600 font-medium">Guide</span>
              </nav>

              <span className="inline-block text-xs font-medium bg-primary-100 text-primary-700 px-3 py-1 rounded-full mb-4">
                Ultimate Guide
              </span>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-5">
                How to Use Sugargoo Spreadsheet: The Complete 2026 Guide
              </h1>

              <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
                The Sugargoo spreadsheet has revolutionized the way shoppers discover and purchase products through the Sugargoo platform. Whether you are a complete beginner or an experienced buyer, this comprehensive guide will walk you through everything you need to know about using Sugargoo spreadsheets effectively in 2026.
              </p>

              <div className="flex items-center gap-4 text-xs text-foreground-400">
                <span>Updated: June 2026</span>
                <span>&middot;</span>
                <span>12 min read</span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-12 md:py-16">
          <div className="section-container">
            <div className="max-w-3xl mx-auto prose-custom">
              {/* What is a Sugargoo Spreadsheet */}
              <section className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                  What Is a Sugargoo Spreadsheet?
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  A Sugargoo spreadsheet is a meticulously organized digital document that compiles direct product links, pricing information, seller ratings, and quality assessments for items available through the Sugargoo shopping agent platform. These spreadsheets serve as curated catalogs that help shoppers efficiently navigate the vast marketplace without having to manually search through individual listings.
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  The beauty of a well-maintained Sugargoo spreadsheet lies in its structure. Products are typically organized by categories such as Shoes, Hoodies, T-Shirts, Jackets, and Accessories, with each entry containing essential information like the product name, direct purchase link, estimated price range, seller reputation, and sometimes even QC (Quality Check) photo references. This eliminates the guesswork and makes the shopping experience significantly more streamlined.
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  For the 2026 edition, the best Sugargoo spreadsheets include additional features like color-coded seller trust ratings, size availability indicators, and direct links to alternative listings in case the primary link goes out of stock. These enhancements make it easier than ever to find exactly what you are looking for while minimizing the risk of purchasing from unreliable sources.
                </p>
              </section>

              {/* Benefits */}
              <section className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                  Why You Should Use a Sugargoo Spreadsheet
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {[
                    { title: 'Save Hours of Searching', desc: 'Instead of browsing through hundreds of individual product pages, a spreadsheet puts everything in one organized place with direct links.' },
                    { title: 'Verified Seller Links', desc: 'The best spreadsheets only include products from trusted sellers with proven track records of quality and reliability.' },
                    { title: 'Price Comparison Made Easy', desc: 'See prices side-by-side across different sellers and categories to find the best deals on the items you want.' },
                    { title: 'Up-to-Date Inventory', desc: 'Regularly updated spreadsheets mean you are always browsing active listings rather than dead or outdated links.' },
                  ].map((item) => (
                    <div key={item.title} className="bg-background-100 rounded-xl p-5">
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{item.title}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* How to Use */}
              <section className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                  Step-by-Step: How to Use the Sugargoo Spreadsheet
                </h2>

                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="font-bold text-primary-700 text-sm">1</span>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Open the Spreadsheet and Browse Categories</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        Start by accessing the latest Sugargoo spreadsheet. The document is organized into clearly labeled category tabs — Shoes, Hoodies, T-Shirts, Jackets, Bags, Accessories, and more. Navigate to the category you are interested in to see all available listings. Each row represents a unique product with its own set of details.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="font-bold text-primary-700 text-sm">2</span>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Review Product Details and Seller Ratings</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        Before clicking any link, review the product details column. Look at the estimated price range, seller rating, and any notes about sizing or quality. Trusted spreadsheets use a rating system — often color-coded — to indicate seller reliability. Green means highly trusted, yellow means use caution, and red or unrated sellers should generally be avoided.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="font-bold text-primary-700 text-sm">3</span>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Click the Product Link and Copy to Sugargoo</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        Once you have found an item you want, click the direct product link in the spreadsheet. This will open the product page on the source platform. Copy the URL and paste it into the Sugargoo search bar on their website or app. Sugargoo will then pull up the product details and allow you to add it to your cart.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="font-bold text-primary-700 text-sm">4</span>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Place Your Order and Wait for QC Photos</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        After adding the item to your Sugargoo cart, complete the purchase. The seller will ship the product to the Sugargoo warehouse. Once it arrives, Sugargoo will take QC (Quality Check) photos and upload them to your account. Review these photos carefully — check for any defects, color mismatches, or sizing issues before approving the item for international shipping.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="font-bold text-primary-700 text-sm">5</span>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Choose Shipping and Receive Your Package</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        If the QC photos look good, approve the item for shipping. Sugargoo offers multiple shipping lines with different price points and delivery speeds. Choose the one that best fits your budget and timeline. Once shipped, track your package and wait for it to arrive at your doorstep.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Safety Tips */}
              <section className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                  Sugargoo Spreadsheet Safety Tips
                </h2>

                <div className="bg-accent-50 border border-accent-200/50 rounded-xl p-6 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-xl text-accent-600"></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Always Check Seller History</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        Never purchase from a seller with zero ratings or negative reviews, even if the price seems unbeatable. Stick to sellers who have been verified by the spreadsheet curator and have a consistent history of delivering quality products.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-secondary-50 border border-secondary-200/50 rounded-xl p-6 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className="ri-image-line text-xl text-secondary-600"></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Review QC Photos Thoroughly</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        QC photos are your last line of defense before international shipping. Zoom in on every detail — stitching, logos, colors, and materials. If something looks off, request additional photos or a return. Once the item ships internationally, returns become much more difficult.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-primary-50 border border-primary-200/50 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className="ri-scales-line text-xl text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Understand Size Charts Before Ordering</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        Asian sizing often runs smaller than Western sizing. Always check the size chart provided by the seller and compare it against your own measurements. When in doubt, size up. Many spreadsheets include sizing notes to help you make the right choice.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Categories */}
              <section className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                  Sugargoo Spreadsheet Categories Overview
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  The Sugargoo spreadsheet covers an extensive range of categories. Here is a quick overview of what you can expect to find in each major section:
                </p>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200">
                        <th className="text-left py-3 px-4 font-semibold text-foreground-900">Category</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground-900">Typical Price Range</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground-900">Popular Items</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground-900">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { cat: 'Shoes', price: '$25 - $80', items: 'Sneakers, Slides, Boots', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
                        { cat: 'Hoodies / Sweaters', price: '$15 - $55', items: 'Pullovers, Zip-ups, Crewnecks', url: 'https://kaspreadsheet.com/category-912-Hoodies%2FSweaters.html' },
                        { cat: 'T-Shirts', price: '$8 - $30', items: 'Graphic Tees, Plain, Long Sleeve', url: 'https://kaspreadsheet.com/category-913-T-Shirts.html' },
                        { cat: 'Jackets', price: '$30 - $100', items: 'Bombers, Denim, Puffers', url: 'https://kaspreadsheet.com/category-914-Jackets.html' },
                        { cat: 'Bags', price: '$20 - $70', items: 'Backpacks, Totes, Crossbody', url: 'https://kaspreadsheet.com/category-921-Bags.html' },
                        { cat: 'Accessories', price: '$5 - $40', items: 'Watches, Sunglasses, Jewelry', url: 'https://kaspreadsheet.com/category-920-Accessories.html' },
                      ].map((row) => (
                        <tr key={row.cat} className="border-b border-background-100 hover:bg-background-100/50 transition-colors">
                          <td className="py-3 px-4 font-medium text-foreground-900">{row.cat}</td>
                          <td className="py-3 px-4 text-foreground-600">{row.price}</td>
                          <td className="py-3 px-4 text-foreground-600">{row.items}</td>
                          <td className="py-3 px-4">
                            <a href={row.url} target="_blank" rel="noopener nofollow" className="text-primary-600 font-medium hover:text-primary-700 whitespace-nowrap">
                              Browse <i className="ri-arrow-right-up-line text-xs"></i>
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* FAQ */}
              <section className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                  Frequently Asked Questions
                </h2>

                <div className="space-y-4">
                  <div className="bg-background-100 rounded-xl p-5">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Is the Sugargoo spreadsheet free to use?</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      Yes, the Sugargoo spreadsheet is completely free to browse and use. You do not need to pay anything to access the product links. The only costs involved are the actual product prices and shipping fees when you decide to make a purchase through Sugargoo.
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-xl p-5">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">How often are the spreadsheet links updated?</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      The best Sugargoo spreadsheets are updated on a weekly or even daily basis. Product links can go inactive quickly, so regular updates are crucial. Our featured spreadsheet is refreshed frequently to ensure maximum link validity and to add new finds as they become available.
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-xl p-5">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Can I request specific items to be added?</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      While spreadsheet curators typically select products based on community demand and seller reliability, many are open to suggestions. The best way to find specific items is to use Sugargoo image search or browse through the categories in the spreadsheet for similar products.
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-xl p-5">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">What happens if a product link is dead?</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      Dead links are a common occurrence due to sellers updating their listings. If you encounter a dead link, check the spreadsheet for alternative links to the same product (many curators include backup links). You can also use Sugargoo image search with the product photo to find the same item from other sellers.
                    </p>
                  </div>
                </div>

                {/* FAQ Schema */}
                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                      '@context': 'https://schema.org',
                      '@type': 'FAQPage',
                      mainEntity: [
                        { '@type': 'Question', name: 'Is the Sugargoo spreadsheet free to use?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, the Sugargoo spreadsheet is completely free to browse and use. You do not need to pay anything to access the product links.' } },
                        { '@type': 'Question', name: 'How often are the spreadsheet links updated?', acceptedAnswer: { '@type': 'Answer', text: 'The best Sugargoo spreadsheets are updated on a weekly or even daily basis. Product links can go inactive quickly, so regular updates are crucial.' } },
                        { '@type': 'Question', name: 'Can I request specific items to be added?', acceptedAnswer: { '@type': 'Answer', text: 'While spreadsheet curators typically select products based on community demand, many are open to suggestions. Use Sugargoo image search for specific items.' } },
                        { '@type': 'Question', name: 'What happens if a product link is dead?', acceptedAnswer: { '@type': 'Answer', text: 'Dead links are common. Check for alternative links in the spreadsheet or use Sugargoo image search with the product photo to find the same item from other sellers.' } },
                      ],
                    }),
                  }}
                />
              </section>

              {/* Back to Home CTA */}
              <div className="text-center pt-8 border-t border-background-200/70">
                <p className="text-sm text-foreground-500 mb-4">Ready to start exploring?</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-home-line"></i>
                    Back to Homepage
                  </Link>
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 bg-foreground-900 text-background-50 px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer"
                  >
                    View Spreadsheet
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            '@id': `${SITE_URL}/sugargoo-spreadsheet-guide#article`,
            headline: 'How to Use Sugargoo Spreadsheet: The Complete 2026 Guide',
            description: 'A comprehensive guide covering everything about using Sugargoo spreadsheets — from finding products to QC photos, shipping, and safety tips.',
            url: `${SITE_URL}/sugargoo-spreadsheet-guide`,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `${SITE_URL}/sugargoo-spreadsheet-guide`,
            },
            author: { '@type': 'Organization', name: 'Sugargoo Spreadsheet Watch', url: SITE_URL },
            datePublished: '2026-06-01',
            dateModified: '2026-06-18',
            publisher: {
              '@type': 'Organization',
              name: 'Sugargoo Spreadsheet Watch',
              url: SITE_URL,
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg',
              },
            },
          }),
        }}
      />

      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
              { '@type': 'ListItem', position: 2, name: 'Sugargoo Spreadsheet Guide', item: `${SITE_URL}/sugargoo-spreadsheet-guide` },
            ],
          }),
        }}
      />
    </div>
  );
}